import React, { useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type {
  LaboratoryResultSamplingQuestion,
  LaboratoryResultSamplingSnapshot,
} from '../../api/strapiCrm';
import { parseFormAnswerFileRef } from '../../api/formSampling';
import { strapiMediaUrl } from '../../api/strapi';
import { ChemInline } from '../../components/ChemInline';
import {
  formatSnapshotAnswerValue,
  groupSnapshotQuestionsForRender,
  samplingFotoHref,
  snapshotQuestionCellValue,
} from './laboratorySamplingSnapshotDisplay';
import { matrixRangeCaption, type MatrixColumn } from '../SurveyPage/components/surveyFormQuestionMatrix';

function formatDateTime(iso?: string): string {
  if (!iso?.trim()) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.trim();
  return d.toLocaleString('id-ID', { dateStyle: 'short', timeStyle: 'short' });
}

function SnapshotRow({ label, value }: { label: string; value?: string | null }) {
  const text = String(value ?? '').trim();
  if (!text) return null;
  return (
    <Typography variant="body2">
      <Box component="span" sx={{ color: 'text.secondary' }}>
        {label}:{' '}
      </Box>
      {text}
    </Typography>
  );
}

export function LaboratorySamplingFotoLink({
  fotoUrl,
  children = 'Lihat foto',
  testId,
}: {
  fotoUrl?: string | null;
  children?: React.ReactNode;
  testId?: string;
}) {
  const [open, setOpen] = useState(false);
  const href = samplingFotoHref(fotoUrl);
  if (!href) return null;
  return (
    <>
      <Link
        href={href}
        underline="hover"
        data-testid={testId}
        onClick={(e) => {
          e.preventDefault();
          setOpen(true);
        }}
      >
        {children}
      </Link>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        data-testid="laboratory-sampling-foto-preview-dialog"
      >
        <DialogTitle sx={{ pr: 6 }}>
          Foto
          <IconButton
            aria-label="Tutup"
            onClick={() => setOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: 'grey.100',
            minHeight: 240,
          }}
        >
          <Box
            component="img"
            src={href}
            alt="Foto"
            sx={{
              maxWidth: '100%',
              maxHeight: { xs: '70vh', md: '75vh' },
              objectFit: 'contain',
              display: 'block',
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

function SnapshotLinearQuestion({ question }: { question: LaboratoryResultSamplingQuestion }) {
  const file = parseFormAnswerFileRef(question.value);
  const value = formatSnapshotAnswerValue(question.value, question.answer_type);
  if (!value) return null;
  return (
    <Typography variant="body2">
      <Box component="span" sx={{ color: 'text.secondary' }}>
        <ChemInline text={question.title} />
        {': '}
      </Box>
      {file?.url ? (
        <Link href={strapiMediaUrl(file.url)} target="_blank" rel="noreferrer" underline="hover">
          {value}
        </Link>
      ) : (
        value
      )}
    </Typography>
  );
}

function SnapshotMatrix({
  questions,
  columns,
}: {
  questions: LaboratoryResultSamplingQuestion[];
  columns: MatrixColumn[];
}) {
  const rangeCaption = matrixRangeCaption(columns);
  return (
    <Box>
      {rangeCaption ? (
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          <ChemInline text={rangeCaption} />
        </Typography>
      ) : null}
      <TableContainer
        sx={{ overflowX: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}
      >
        <Table size="small" sx={{ minWidth: 160 + columns.length * 72 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, minWidth: 140, py: 0.5 }}>Pertanyaan</TableCell>
              {columns.map((col) => (
                <TableCell key={col.key} align="center" sx={{ fontWeight: 600, minWidth: 56, py: 0.5 }}>
                  <ChemInline text={col.title} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {questions.map((question) => (
              <TableRow key={question.document_id}>
                <TableCell sx={{ py: 0.5 }}>
                  <ChemInline text={question.title} />
                </TableCell>
                {columns.map((col) => {
                  const value = snapshotQuestionCellValue(question, col.key);
                  return (
                    <TableCell key={col.key} align="center" sx={{ py: 0.5 }}>
                      {value || '—'}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export function LaboratorySamplingSnapshot({ sampling }: { sampling?: LaboratoryResultSamplingSnapshot }) {
  if (!sampling) return null;
  const fotoHref = samplingFotoHref(sampling.foto_url);
  const hasMeta =
    Boolean(sampling.start_time) ||
    Boolean(sampling.end_time) ||
    Boolean(sampling.sample_collection_datetime) ||
    Boolean(sampling.jumlah_sampel) ||
    Boolean(sampling.abnormalitas_note) ||
    Boolean(fotoHref);
  const hasMeasurements = sampling.field_measurements.length > 0;
  const hasAnswers = sampling.form_answer_groups.some((g) => (g.questions?.length ?? 0) > 0);
  if (!hasMeta && !hasMeasurements && !hasAnswers) return null;

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: 'grey.50' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75 }}>
        Isian petugas sampling
      </Typography>
      <Stack spacing={0.25}>
        <SnapshotRow label="Waktu mulai" value={formatDateTime(sampling.start_time)} />
        <SnapshotRow label="Waktu selesai" value={formatDateTime(sampling.end_time)} />
        <SnapshotRow
          label="Tanggal pengambilan"
          value={formatDateTime(sampling.sample_collection_datetime)}
        />
        <SnapshotRow label="Jumlah sampel" value={sampling.jumlah_sampel} />
        <SnapshotRow label="Catatan abnormalitas" value={sampling.abnormalitas_note} />
        {fotoHref ? (
          <Typography variant="body2">
            <Box component="span" sx={{ color: 'text.secondary' }}>
              Foto:{' '}
            </Box>
            <LaboratorySamplingFotoLink
              fotoUrl={sampling.foto_url}
              testId="laboratory-sampling-foto-link"
            />
          </Typography>
        ) : null}
      </Stack>
      {hasMeasurements ? (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
            Parameter lapangan
          </Typography>
          {sampling.field_measurements.map((item) => (
            <SnapshotRow key={item.label} label={item.label} value={item.value} />
          ))}
        </Box>
      ) : null}
      {sampling.form_answer_groups.map((group) =>
        (group.questions?.length ?? 0) ? (
          <Box key={group.form_question_name} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              {group.form_question_name}
            </Typography>
            <Stack spacing={1}>
              {groupSnapshotQuestionsForRender(group.questions).map((block, idx) =>
                block.kind === 'linear' ? (
                  <SnapshotLinearQuestion key={block.question.document_id} question={block.question} />
                ) : (
                  <SnapshotMatrix
                    key={`matrix-${block.questions.map((q) => q.document_id).join('-') || idx}`}
                    questions={block.questions}
                    columns={block.columns}
                  />
                ),
              )}
            </Stack>
          </Box>
        ) : null,
      )}
    </Paper>
  );
}
